import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ContactRequestQuotationWithDetail } from '../contact-request-quotation-with-detail';
import { ContactRequestService } from '../service/contact-request.service';
import { CreateContactRequestQuotationComponent } from '../create-contact-request-quotation/create-contact-request-quotation.component';
import { EditContactRequestComponent } from '../edit-contact-request/edit-contact-request.component';
import { HttpService } from '../service/http.service';
import { LeadQuotationService } from '../service/lead-quotation.service';
import { ProgrammeInformation } from '../programme-information';
import { ToastMessageService } from '../toast-message.service';
import { MenuItem } from 'primeng/api';
import { LeadType } from '../lead-type';
import { FormArray } from '@angular/forms';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';
import { Country } from '../country';
import { ApiError } from '../api-error';
import { BrevoList } from '../brevo-list';
import { BrevoContact } from '../brevo-contact';

@Component({
  selector: 'app-contact-request-edit-navigation',
  templateUrl: './contact-request-edit-navigation.component.html',
  styleUrls: ['./contact-request-edit-navigation.component.css'],
  providers: [ ContactRequestService, LeadQuotationService]
})
export class ContactRequestEditNavigationComponent implements OnInit, OnDestroy {

  @ViewChild("editComponent") editComponent!: EditContactRequestComponent;
  @ViewChild("quotationComponent") public quotationComponent!: CreateContactRequestQuotationComponent;

  requestId!: number;
  navigationItems: MenuItem[] = [];
  activeItemId = 1;
  activeItem!: MenuItem;
  selectedQuotation!: ContactRequestQuotationWithDetail;
  selectedLanguageName!: string;
  isDeleted = false;
  leadType: LeadType | null = null;
  editProgrammeUrls = false;

  brevoErrorMessage: string | null = null;
  hasBrevoErrorMessage: boolean = false;
  isConnectingToBrevoServer: boolean = false;
  brevoLists: BrevoList[] = [];
  targetBrevoLists: BrevoList[] = [];
  listBoxDisabled: boolean = false;
  isCreatingBrevoContact: boolean = false;
  displayBrevoDialog: boolean = false;
  selectedBrevoContact: BrevoContact | null = null;
  isUpdatingBrevoContact: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private toastService: ToastMessageService,
    public leadQuotationService: LeadQuotationService,
    public contactRequestService: ContactRequestService,
    private httpService: HttpService
  ) {}

  ngOnInit(): void {
    this.requestId = parseInt(this.route.snapshot.paramMap.get("requestId")!);
    this.contactRequestService.sendingConstantContact = false;
    forkJoin({
      contactRequest: this.httpService.getContactRequestById(this.requestId),
      countries: this.httpService.getAllCountries(),
      schools: this.httpService.getSchoolsWithCountries()
    }).subscribe(({contactRequest, countries, schools}) => {
      this.contactRequestService.countries = countries;
      this.contactRequestService.filteredCountries = countries;

      this.contactRequestService.schools = schools;
      this.contactRequestService.filteredSchools = schools;

      if(contactRequest.destination){
        this.filterSchoolsByCountry(contactRequest.destination);
      }
      if(contactRequest.school){
        this.httpService.getProgrammeInformationsBySchoolAndLanguage(contactRequest.school.id!, contactRequest.language.id).subscribe(programmes => {
          this.contactRequestService.programmeInformations = programmes;
        });
        this.httpService.getCountriesBySchoolId(contactRequest.school.id!).subscribe(countries => {
          this.fillterCountries(countries);
        });
      }
      if(contactRequest.startDate){
        contactRequest.startDate = moment(contactRequest.startDate);
      }
      if(contactRequest.endDate){
        contactRequest.endDate = moment(contactRequest.endDate);
      }
      if(contactRequest.birthDate){
        contactRequest.birthDate = moment(contactRequest.birthDate);
      }
      this.contactRequestService.contactRequest = contactRequest;
      this.leadType = contactRequest.leadType;
      this.contactRequestService.initRequestForm();
      this.httpService.getCounsellors().subscribe(res => {
        this.contactRequestService.counsellors = res;
        this.contactRequestService.setCounsellors();
      });
      let canShowNewQuotationTab = true;
      if(this.contactRequestService.contactRequest?.isDeleted){
        canShowNewQuotationTab = false;
      }
      this.navigationItems = [
        { id: "1", label: "Lead", command: () => this.activeItemId = 1 },
        { id: "2", label: "New quotation", command: () => this.activeItemId = 2, visible: canShowNewQuotationTab },
        {
          id: "4", label: "All quotations", command: () => {
            this.activeItemId = 4;
            this.activeItem = this.navigationItems[3];
          }
        }
      ];
      this.activeItem = this.navigationItems[0];
    });
    this.httpService.getLeadStatuses().subscribe(res => {
      this.contactRequestService.leadStatuses = res;
    });
  }

  public activateBrevoDialog() {
    this.brevoErrorMessage = null;
    this.hasBrevoErrorMessage = false;
    this.displayBrevoDialog = true;
    const contactRequest = this.contactRequestService.contactRequest!;
    if (!contactRequest!.email) {
      console.error("this student has no email. cannot get brevo contact");
      this.brevoErrorMessage = "This student has no email. Please create one.";
      this.hasBrevoErrorMessage = true;
    } else {
      this.isConnectingToBrevoServer = true;
      
      this.httpService.getBrevoContactByEmail(contactRequest.email, contactRequest.firstName, contactRequest.lastName).subscribe(res => {
        this.selectedBrevoContact = res;
        this.httpService.getBrevoLists().subscribe(res => {
          this.brevoLists = res.lists;
          this.initBrevoList();
          this.isConnectingToBrevoServer = false;
        }, error => {
          let apiError = new ApiError(error);
          this.toastService.addApiError(apiError);
          this.isConnectingToBrevoServer = false;
        });
      }, error => {
        let apiError = new ApiError(error);
        this.toastService.addApiError(apiError);
        this.isConnectingToBrevoServer = false;
      });
    }
  }

  initBrevoList() {
    this.targetBrevoLists = [];
    for (let listId of this.selectedBrevoContact?.listIds!) {
      for (let list of this.brevoLists) {
        if (list.id === listId) {
          this.targetBrevoLists.push(list);
        }
      }
    }
  }

  selectBrevoList(target: BrevoList) {
    this.listBoxDisabled = true;
    for (let list of this.targetBrevoLists) {
      if (list.id === target.id) {
        this.listBoxDisabled = false;
        return;
      }
    }
    //if contact does not exist, create a new contact
    //if the contact already exists, update the contact with new lists
    this.targetBrevoLists.push(target);
    if (this.isCreatingBrevoContact) {
      // this.addConstantContact();
    } else {
      this.updateBrevoContact();
    }
    this.listBoxDisabled = false;
  }

  removeBrevoList(selectedList: BrevoList) {
    this.targetBrevoLists = this.targetBrevoLists.filter(list => {
      return list.id != selectedList.id;
    });
    this.updateBrevoContact();
  }

  updateBrevoContact() {
    if(!this.isUpdatingBrevoContact){
      let contact = this.selectedBrevoContact;
      contact!.listIds = [];
      for(let list of this.targetBrevoLists){
        contact!.listIds.push(list.id);
      }
      this.isUpdatingBrevoContact = true;
      this.httpService.updateBrevoContact(this.selectedBrevoContact!, this.selectedBrevoContact?.email!).subscribe(() => {
        this.toastService.addSuccess("Success !", "Contact updated");
        this.isUpdatingBrevoContact = false;
      }, error => {
        this.isUpdatingBrevoContact = false;
      });
    }
  }

  copyQuotation(selectedQuotation: ContactRequestQuotationWithDetail) {
    this.activeItemId = 2;
    this.activeItem = this.navigationItems[1];
    this.selectedQuotation = selectedQuotation;
  }

  updateTabs(isDeleted: boolean) {
    if (isDeleted) {
      this.navigationItems[1].visible = false;
      this.navigationItems[2].visible = false;
      this.navigationItems[3].visible = false;
    }
  }

  ngOnDestroy() {
    this.leadQuotationService.quotationForm = null;
    this.contactRequestService.contactRequest = null;
  }

  updateRequest() {
    this.contactRequestService.isSendingRequest = true;
    this.editComponent.updateRequestInformation();
  }

  activateProgrammeUrlEditWindow(){
    this.editProgrammeUrls = true;
    this.contactRequestService.initProgrammeInformationForm();
  }

  addProgrammeUrls(){
    let school = this.contactRequestService.contactRequestForm.value.school;
    let informations = [];
    for(let control of this.contactRequestService.programmeInformationForm.controls){
      if(control.value.url && control.value.url.length > 0){
        informations.push(new ProgrammeInformation(
          control.value.id,
          control.value.name,
          control.value.language,
          control.value.url,
          control.value.label
        ));
      }
    }
    this.httpService.postProgrammeInformations(informations, school.id).subscribe(() => {
      this.toastService.addSuccess("Success", "URLs updated");
      this.editProgrammeUrls = false;
    }, error => this.toastService.addError("Error", error.message));
  }

  get usersFormArray(){
    let formArray = this.contactRequestService.requestForm.get('usersInCharge') as FormArray;
    return formArray.controls;
  }

  filterSchoolsByCountry(selectedCountry: Country) {
    this.contactRequestService.filteredSchools = [];
    for (let school of this.contactRequestService.schools) {
      for (let countryId of school.countries) {
        if (selectedCountry.id === countryId) {
          this.contactRequestService.filteredSchools.push(school);
        }
      }
    }
  }

  fillterCountries(countries: Country[]) {
    this.contactRequestService.filteredCountries = [];
    for (let destination of this.contactRequestService.countries) {
      for (let country of countries) {
        if (destination.id === country.id) {
          this.contactRequestService.filteredCountries.push(destination);
        }
      }
    }
  }

}
